import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { FileInfo } from '@progress/kendo-angular-upload';
import { DocumentStatus } from '@/core/enums/document-status.enum';
import { AnfrageService, DocumentService, PortalkonfigurationService } from '@/api/service-index';
import { PortalkonfigurationDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/portalkonfiguration-display-model';
import { DocumenttypeDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/documenttype-display-model';
import { DocumentDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/document-display-model';
import { FileTooLargeNotification } from '@/core/utils/notifications/document/file-too-large.notification';
import { InvalidMimeTypeNotification } from '@/core/utils/notifications/document/invalid-mime-type.notification';
import { StateService } from '@/core/services/state.service';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { DocumentService as ClientDocumentService } from '@/core/services/document.service';

@Component({
  selector: 'finanzplan-document',
  templateUrl: './finanzplan-document.component.html',
  styleUrls: ['./finanzplan-document.component.scss']
})
export class FinanzplanDocumentComponent implements OnInit {

  @Input()
  public finanzplanDocumentId: string;

  @Input()
  public formGroup: FormGroup;

  protected portalkonfiguration: PortalkonfigurationDisplayModel;  
  protected onChange: any = () => { };
  protected onTouched: any = () => { };
  public originalDocuments: DocumentDisplayModel[];
  protected _documentType: DocumenttypeDisplayModel;
  protected _type: string;

  protected _disabledState: boolean;
  protected _documentStatus: DocumentStatus;

  @Output('documentUploaded') documentUploadedEventEmitter: EventEmitter<DocumentDisplayModel> = new EventEmitter<DocumentDisplayModel>();

  public get documentType(): DocumenttypeDisplayModel {
    return this._documentType;
  }

  public get documentStatus(): DocumentStatus | undefined {
    if (this.finanzplanDocumentId)
      return DocumentStatus.Uploaded;

    return undefined;
  }

  @Input()
  public set documentType(documentType: DocumenttypeDisplayModel) {
    this._documentType = documentType;
  }

  public get projectType(): string {
    return this._type;
  }

  public get isTemplateDownloadButtonVisible(): boolean {
    return this._documentType?.hasVorlage != null && this._documentType.hasVorlage;
  }

  public get isDocumentStatusUndefined(): boolean {
    return this.documentStatus == undefined;
  }

  public get isDocumentUploaded(): boolean {
    return this.documentStatus == DocumentStatus.Uploaded;
  }

  public get isDownloadButtonVisible(): boolean {
    return this.finanzplanDocumentId != '' && !this.isDocumentStatusUndefined && this.isDocumentUploaded;
  }

  public get acceptedFileFormats(): string {
    if (this.documentType) {
      let accepted: string = "";
      this.documentType.erlaubteDateiendungen?.forEach(ext => {
        accepted += "." + ext + ","
      });
      return accepted.substr(0, accepted.lastIndexOf(","));
    }
    return "*.*";
  }

  public get isOpenDocumentButtonDisabled(): boolean {
    return this.isDisabled;
  }

  public get isDisabled(): boolean {
    return this._disabledState;
  }

  constructor(protected stateService: StateService,
    protected loadingAnimationService: LoadingAnimationService,
    protected anfrageService: AnfrageService, 
    protected documentService: DocumentService,
    protected clientDocumentService: ClientDocumentService,
    protected route: ActivatedRoute, 
    protected dialogService: DialogService, 
    protected portalContextService: PortalContextService,
    protected portalkonfigurationService: PortalkonfigurationService) {

  }

  ngOnInit(): void {
    this.portalkonfiguration = this.route.snapshot.data.portalkonfiguration;

    this.setDisabledState(this.formGroup.disabled);
  }

  public onValueChange(event: FileInfo[]) {
    this.onChange();
    this.onTouched();
  }

  public onFileUploadChange(event: any) {
    this._uploadDocument(event);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._disabledState = isDisabled;
  }

  public onTemplateDownloadDocumentClick() {
    this.downloadTemplate();
  }

  public onDownloadDocumentClick() {
    this.downloadDocument();
  }

  public downloadTemplate() {
    if (this._documentType.hasVorlage && this.portalContextService.CurrentItemContext != null) {
      let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentVorlageDocumententypIdGet({ anfrageId: this.portalContextService.CurrentItemContext.id, documententypId: this._documentType.documenttypeId! })
        .subscribe(file => {
          this.clientDocumentService.download(file)
        });

      if(subscription)
        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  public downloadDocument() {
    if (this.finanzplanDocumentId && this.portalContextService.CurrentItemContext != null) {
      let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentDocumentIdGet({ anfrageId: this.portalContextService.CurrentItemContext.id, documentId: this.finanzplanDocumentId, loadContent: true  })
        .subscribe(file => {
          this.clientDocumentService.download(file)
        });

      if(subscription)
        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  protected uploadFinanzplan(document: DocumentDisplayModel) {
    this._documentStatus = DocumentStatus.New;

    if (this.portalContextService.CurrentItemContext != null) {
      let subscription = this.documentService.apiDocumentEntityIdUploadFinanzplanPost({ entityId: this.portalContextService.CurrentItemContext.id, body: document })
        .subscribe(response => {
          this._documentStatus = DocumentStatus.Uploaded;

          if (response) {
            let docModel: DocumentDisplayModel = new DocumentDisplayModel();
            docModel.documentId = response;
            this.finanzplanDocumentId = response;
            this.documentUploadedEventEmitter.emit(docModel);
          }
        });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  protected _uploadDocument(event: any) {
    const reader = new FileReader();
    let fileToUpload: File = event.target.files[0];
    let fileSizeInMB = fileToUpload.size / 1024 / 1024;

    if (this.portalkonfiguration.maxFileSizeMB && fileSizeInMB > this.portalkonfiguration.maxFileSizeMB) {
      throw new FileTooLargeNotification(`File is too large. Max allowed filesize: ${this.portalkonfiguration.maxFileSizeMB} MB. Current File size ${fileSizeInMB} MB`, `Die ausgewählte Datei ist zu groß. Maximal zulässige Dateigröße: ${this.portalkonfiguration.maxFileSizeMB} MB`);
    }

    let document: DocumentDisplayModel = new DocumentDisplayModel();

    document.name = event.target.files[0].name;
    document.contentMimeType = event.target.files[0].type;
    document.documententyp = this.documentType;

    if (!this.documentType.erlaubteDateiendungen?.includes(this.documentFileExtension(document))) {
      throw new InvalidMimeTypeNotification("Invalid mimetype for document");
    }

    reader.onload = (e: any) => {
      if (!e.target.result)
        return;

      let base64String: string = (<string>e.target.result).split(",")[1];

      let byteCharacters = atob(base64String);

      document.content = base64String;
      this.uploadFinanzplan(document);
      return;
    };

    reader.readAsDataURL(fileToUpload);
  }

  public documentFileExtension(document: DocumentDisplayModel): string {
    if (document.name && document.name.indexOf(".") != -1) {
      return document.name.substr(document.name.lastIndexOf(".") + 1);
    }
    return "";
  }
}
